import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api, API_BASE } from "@/lib/api";
import { Loader2, Mic, Pause, Play, Sparkles, Flame, Video, VideoOff, X } from "lucide-react";
import { toast } from "sonner";

const guestId = () => {
  let id = localStorage.getItem("amigos_guest_id");
  if (!id) {
    id = `g_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
    localStorage.setItem("amigos_guest_id", id);
  }
  return id;
};

// iOS keeps the audio session in "record" mode while a mic stream is open, which mutes
// playback routed through the Web Audio graph: there we play plainly and fake the mouth.
const IS_IOS = /iP(hone|ad|od)/.test(navigator.userAgent)
  || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

// A 50 ms silent clip: playing it inside the click that opens the chat unlocks audio on iOS,
// so every later reply can play on its own (browsers forget the "user gesture" after awaits).
const SILENT_WAV = "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAgD4AAAB9AAACABAAZGF0YQAAAAA=";

let sharedAudio = null;
let sharedCtx = null;
let sharedAnalyser = null;

/** Call this synchronously from a click: creates and unlocks the single audio player. */
export const unlockAmigosAudio = () => {
  if (!sharedCtx) {
    try { sharedCtx = new (window.AudioContext || window.webkitAudioContext)(); } catch { sharedCtx = null; }
  }
  sharedCtx?.resume?.().catch(() => {});
  if (!sharedAudio) {
    sharedAudio = new Audio();
    sharedAudio.crossOrigin = "anonymous";
    sharedAudio.preload = "auto";
    if (!IS_IOS && sharedCtx) {
      try {
        const src = sharedCtx.createMediaElementSource(sharedAudio);
        const an = sharedCtx.createAnalyser();
        an.fftSize = 512;
        src.connect(an);
        an.connect(sharedCtx.destination);
        sharedAnalyser = an;
      } catch { sharedAnalyser = null; }
    }
  }
  sharedAudio.src = SILENT_WAV;
  sharedAudio.play().catch(() => {});
  return sharedAudio;
};

const SILENCE_MS = 1300;     // how long the child can pause before we send the audio
const MIN_SPEECH_MS = 700;
const MAX_TURN_MS = 20000;
const SPEECH_LEVEL = 0.045;

/** Face-to-face voice practice: the friend up top (animated mouth), the student's camera below. */
export const AmigosChat = ({ character, topicId = "surprise", onBack }) => {
  const [session, setSession] = useState(null);
  const [phase, setPhase] = useState("loading");   // loading | idle | listening | thinking | speaking
  const [caption, setCaption] = useState({ es: "", en: "", who: "amigo" });
  const [stats, setStats] = useState({ stars: 0, streak: 0, turns_today: 0, turn_limit: 8 });
  const [words, setWords] = useState([]);
  const [handsFree, setHandsFree] = useState(true);
  const [mouth, setMouth] = useState("closed");
  const [camOn, setCamOn] = useState(true);
  const [lastYouSaid, setLastYouSaid] = useState("");
  const [topic, setTopic] = useState(null);
  const [goalsDone, setGoalsDone] = useState([]);
  const [showEn, setShowEn] = useState(true);
  const [needsTap, setNeedsTap] = useState(false);

  const videoRef = useRef(null);
  const camStream = useRef(null);
  const audioCtx = useRef(null);
  const player = useRef(null);        // the one and only <audio>, unlocked once
  const analyserRef = useRef(null);
  const fillerUrl = useRef(null);
  const recorder = useRef(null);
  const micStream = useRef(null);
  const rafId = useRef(null);
  const lastAudio = useRef(null);
  const handsFreeRef = useRef(true);
  const sessionRef = useRef(null);
  const alive = useRef(true);

  useEffect(() => { handsFreeRef.current = handsFree; }, [handsFree]);

  const ctx = () => {
    if (!sharedCtx) unlockAmigosAudio();
    return sharedCtx;
  };

  /* ---------- camera ---------- */
  const startCam = useCallback(async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false });
      camStream.current = s;
      if (videoRef.current) videoRef.current.srcObject = s;
      setCamOn(true);
    } catch {
      setCamOn(false);
    }
  }, []);

  const stopCam = () => {
    camStream.current?.getTracks().forEach((t) => t.stop());
    camStream.current = null;
    setCamOn(false);
  };

  const releaseMic = () => {
    micStream.current?.getTracks().forEach((t) => t.stop());
    micStream.current = null;
  };

  /** One audio element for the whole conversation: once iOS lets it play, it keeps playing. */
  const element = () => {
    if (!sharedAudio) unlockAmigosAudio();
    player.current = sharedAudio;
    analyserRef.current = sharedAnalyser;
    return sharedAudio;
  };

  /* ---------- the friend speaks (mouth follows the volume) ---------- */
  const speak = async (url, onDone) => {
    lastAudio.current = url;
    releaseMic();                       // free the audio session before playing
    const a = element();
    cancelAnimationFrame(rafId.current);
    a.onended = null;
    a.src = `${API_BASE}${url}`;
    if (!IS_IOS) await ctx()?.resume?.().catch(() => {});
    const analyser = sharedAnalyser;
    const data = analyser ? new Uint8Array(analyser.fftSize) : null;
    const tick = () => {
      if (!alive.current) return;
      if (analyser) {
        analyser.getByteTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) { const v = (data[i] - 128) / 128; sum += v * v; }
        const rms = Math.sqrt(sum / data.length);
        setMouth(rms > 0.12 ? "open" : rms > 0.045 ? "mid" : "closed");
      } else {
        // no analyser (iOS): a simple 3-frame loop still reads as "talking"
        const t = Math.floor(Date.now() / 130) % 3;
        setMouth(t === 0 ? "closed" : t === 1 ? "mid" : "open");
      }
      rafId.current = requestAnimationFrame(tick);
    };
    setPhase("speaking");
    a.onended = () => {
      cancelAnimationFrame(rafId.current);
      setMouth("closed");
      onDone?.();
    };
    a.onerror = a.onended;
    try {
      await a.play();
      setNeedsTap(false);
      tick();
    } catch (err) {
      if (err?.name === "AbortError") return;   // another clip took over, nothing to do
      setNeedsTap(true);                        // audio blocked: ask for a single tap
      setPhase("idle");
      onDone?.();
    }
  };

  /* ---------- the student speaks (auto stop on silence) ---------- */
  const listen = useCallback(async () => {
    if (!alive.current) return;
    try {
      await ctx()?.resume?.().catch(() => {});
      const stream = micStream.current?.active
        ? micStream.current
        : (micStream.current = await navigator.mediaDevices.getUserMedia({ audio: true }));
      const analyser = ctx().createAnalyser();
      analyser.fftSize = 512;
      ctx().createMediaStreamSource(stream).connect(analyser);
      const data = new Uint8Array(analyser.fftSize);

      const chunks = [];
      // low bitrate: the upload from a phone is what usually costs the seconds
      let mr;
      try { mr = new MediaRecorder(stream, { audioBitsPerSecond: 32000 }); }
      catch { mr = new MediaRecorder(stream); }
      recorder.current = mr;
      mr.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      mr.onstop = async () => {
        cancelAnimationFrame(rafId.current);
        const blob = new Blob(chunks, { type: mr.mimeType || "audio/webm" });
        if (!spoke || blob.size < 1500) { setPhase("idle"); if (handsFreeRef.current) listen(); return; }
        await send(blob, mr.mimeType || "");
      };
      mr.start();
      setPhase("listening");

      const started = Date.now();
      let spoke = false;
      let lastLoud = Date.now();
      const watch = () => {
        if (!alive.current || mr.state !== "recording") return;
        analyser.getByteTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) { const v = (data[i] - 128) / 128; sum += v * v; }
        const rms = Math.sqrt(sum / data.length);
        const now = Date.now();
        if (rms > SPEECH_LEVEL) { lastLoud = now; if (now - started > 250) spoke = true; }
        const quietEnough = spoke && now - lastLoud > SILENCE_MS && now - started > MIN_SPEECH_MS;
        if (quietEnough || now - started > MAX_TURN_MS) { mr.stop(); return; }
        rafId.current = requestAnimationFrame(watch);
      };
      watch();
    } catch {
      setPhase("idle");
      toast.error("Necesitamos permiso del micrófono para conversar");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async (blob, mimeType = "") => {
    setPhase("thinking");
    if (fillerUrl.current) {
      releaseMic();
      const a = element();
      a.onended = null;
      a.src = `${API_BASE}${fillerUrl.current}`;
      a.play().catch(() => {});
    }
    // Safari records audio/mp4, Chrome webm: Whisper needs the right extension
    const ext = mimeType.includes("mp4") || mimeType.includes("mpeg") ? "m4a"
      : mimeType.includes("ogg") ? "ogg" : "webm";
    const fd = new FormData();
    fd.append("session_id", sessionRef.current);
    fd.append("guest_id", guestId());
    fd.append("audio", blob, `voz.${ext}`);
    try {
      const { data: d } = await api.post("/amigos/turn", fd);
      setLastYouSaid(d.you_said);
      setCaption({ es: d.reply, en: d.reply_en, who: "amigo", correction: d.correction });
      setStats({ stars: d.stars, streak: d.streak, turns_today: d.turns_today, turn_limit: d.turn_limit });
      if (d.goals_done) setGoalsDone(d.goals_done);
      if (d.goal_hit?.length) toast.success("¡Reto logrado!");
      if (d.words?.length) setWords((w) => [...w, ...d.words].slice(-18));
      if (d.audio_url) speak(d.audio_url, () => { if (handsFreeRef.current) listen(); });
      else { setPhase("idle"); if (handsFreeRef.current) listen(); }
    } catch (e) {
      setPhase("idle");
      toast.error(e?.response?.data?.detail || "No pudimos enviar tu voz");
    }
  };

  /* ---------- boot ---------- */
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  useEffect(() => {
    alive.current = true;
    (async () => {
      const { data } = await api.post("/amigos/session",
        { character_id: character.id, guest_id: guestId(), topic_id: topicId });
      if (!alive.current) return;
      sessionRef.current = data.session_id;
      setSession(data);
      setTopic(data.topic || null);
      setStats({ stars: data.stars, streak: data.streak, turns_today: data.turns_today, turn_limit: data.turn_limit });
      setCaption({ es: data.greeting, en: data.greeting_en, who: "amigo" });
      startCam();
      let greetingAudio = null;
      try {
        const fd = new FormData();
        fd.append("session_id", data.session_id);
        const g = await api.post("/amigos/greeting", fd);
        greetingAudio = g.data.audio_url;
        fillerUrl.current = g.data.filler_url || null;
      } catch {
        greetingAudio = null;      // sin voz de bienvenida, pero la conversación arranca igual
      }
      if (!alive.current) return;
      if (greetingAudio) speak(greetingAudio, () => { if (handsFreeRef.current) listen(); });
      else { setPhase("idle"); listen(); }
    })().catch((e) => {
      setPhase("idle");
      toast.error(e?.response?.data?.detail || "No pudimos abrir la conversación");
    });

    return () => {
      alive.current = false;
      cancelAnimationFrame(rafId.current);
      player.current?.pause();
      if (recorder.current?.state === "recording") recorder.current.stop();
      releaseMic();
      camStream.current?.getTracks().forEach((t) => t.stop());
      if (player.current) { player.current.onended = null; player.current.pause(); }
    };
  }, [character.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const pause = () => {
    setHandsFree(false);
    cancelAnimationFrame(rafId.current);
    player.current?.pause();
    if (recorder.current?.state === "recording") recorder.current.stop();
    releaseMic();
    setPhase("idle");
  };

  const resume = () => { setHandsFree(true); if (phase !== "speaking") listen(); };

  const img = mouth === "closed" ? character.image : (character.mouths?.[mouth] || character.image);
  const left = Math.max(0, (stats.turn_limit || 0) - (stats.turns_today || 0));
  const statusText = {
    loading: "Preparando a tu amigo…",
    idle: handsFree ? "Listo" : "En pausa · tocá continuar cuando querás",
    listening: "Te escucho… hablá tranquilo",
    thinking: `${character.name} está pensando…`,
    speaking: `${character.name} está hablando`,
  }[phase];

  return createPortal(
    <div className="fixed inset-0 z-[60] bg-slate-950/95 flex items-center justify-center md:p-6">
      <div className="relative w-full h-full md:max-w-5xl md:h-[86vh] bg-slate-950 md:rounded-3xl overflow-hidden md:shadow-[0_20px_70px_rgba(0,0,0,0.5)] flex flex-col"
           data-testid="amigos-chat">
      {/* the friend fills the screen, like a video call */}
      <div className="relative flex-1 min-h-0 overflow-hidden" data-testid="amigos-stage-friend">
        <img src={img} alt={character.name}
             className={`absolute inset-0 h-full w-full object-cover transition-transform duration-[3500ms] ${
               phase === "speaking" ? "scale-[1.04]" : "scale-100"}`} />
        <img src={character.mouths?.mid} alt="" className="hidden" />
        <img src={character.mouths?.open} alt="" className="hidden" />

        {/* top bar over the image */}
        <div className="absolute top-0 inset-x-0 flex items-center gap-2 px-3 pt-3 pb-8 bg-gradient-to-b from-black/60 to-transparent text-white">
          <span className="font-display text-sm font-bold">{character.name}</span>
          <span className="text-[11px] text-white/60 hidden sm:inline">· {character.animal}</span>
          <span className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-amber-300" data-testid="amigos-stars">
            <Sparkles className="h-3.5 w-3.5" /> {stats.stars}
          </span>
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-orange-300" data-testid="amigos-streak">
            <Flame className="h-3.5 w-3.5" /> {stats.streak}
          </span>
          {onBack && (
            <button onClick={onBack} data-testid="amigos-change-friend"
                    className="h-8 w-8 grid place-items-center rounded-full bg-black/40 hover:bg-black/70">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* the student, small, like a video call */}
        <div className="absolute right-3 top-14 w-24 h-32 md:w-40 md:h-52 rounded-2xl overflow-hidden border-2 border-white/25 bg-slate-900 shadow-lg"
             data-testid="amigos-stage-student">
          <video ref={videoRef} autoPlay playsInline muted
                 className={`h-full w-full object-cover scale-x-[-1] ${camOn ? "" : "hidden"}`} />
          {!camOn && (
            <div className="absolute inset-0 grid place-items-center text-white/40 text-[10px] text-center px-2">
              cámara apagada
            </div>
          )}
          <button onClick={() => (camOn ? stopCam() : startCam())} data-testid="amigos-cam-toggle"
                  className="absolute bottom-1 right-1 h-7 w-7 grid place-items-center rounded-full bg-black/60 text-white">
            {camOn ? <VideoOff className="h-3.5 w-3.5" /> : <Video className="h-3.5 w-3.5" />}
          </button>
        </div>

        {topic && (
          <div className="absolute left-3 top-24 max-w-[55%] space-y-1" data-testid="amigos-topic-banner">
            <span className="inline-block rounded-full bg-black/50 text-white text-[11px] px-2.5 py-1">
              {topic.title}
            </span>
            {(topic.goals || []).map((g) => (
              <span key={g.id}
                    className={`block text-[11px] rounded-full px-2.5 py-1 ${
                      goalsDone.includes(g.id)
                        ? "bg-emerald-500/85 text-white line-through"
                        : "bg-black/40 text-white/80"}`}
                    data-testid={`amigos-goal-${g.id}`}>
                {g.text}
              </span>
            ))}
          </div>
        )}

        {phase === "listening" && (
          <span className="absolute left-3 top-14 inline-flex items-center gap-1.5 rounded-full bg-red-600 text-white text-[11px] font-semibold px-2.5 py-1 animate-pulse">
            <Mic className="h-3 w-3" /> te escucho
          </span>
        )}
        {phase === "thinking" && (
          <span className="absolute left-3 top-14 inline-flex items-center gap-1.5 rounded-full bg-black/60 text-white text-[11px] px-2.5 py-1">
            <Loader2 className="h-3 w-3 animate-spin" /> pensando…
          </span>
        )}

        {/* captions live over the image so everything fits in one screen */}
        <div className="absolute inset-x-0 bottom-0 px-4 pt-10 pb-3 bg-gradient-to-t from-black/85 via-black/60 to-transparent text-white"
             data-testid="amigos-captions">
          {lastYouSaid && (
            <p className="text-xs text-white/55 mb-1 truncate" data-testid="amigos-you-said">“{lastYouSaid}”</p>
          )}
          <p className="text-base md:text-lg font-medium leading-snug line-clamp-3">{caption.es}</p>
          {needsTap && (
            <button data-testid="amigos-tap-to-hear"
                    onClick={() => { setNeedsTap(false); if (lastAudio.current) speak(lastAudio.current, () => { if (handsFreeRef.current) listen(); }); }}
                    className="mt-2 inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-2 text-sm font-semibold">
              Tocá para escuchar a {character.name}
            </button>
          )}
          {caption.en && showEn && (
            <p className="text-sm text-white/60 mt-1 leading-snug line-clamp-2">{caption.en}</p>
          )}
          {caption.correction && (
            <p className="mt-1.5 text-[12px] text-amber-200 line-clamp-2">{caption.correction}</p>
          )}
          {!!words.length && (
            <div className="flex gap-1.5 mt-2 overflow-x-auto no-scrollbar" data-testid="amigos-words">
              {words.slice(-4).map((w, i) => (
                <span key={`${w.es}${i}`} className="shrink-0 text-[11px] rounded-full bg-emerald-500/20 text-emerald-100 border border-emerald-300/30 px-2 py-0.5">
                  {w.es}{w.en ? ` · ${w.en}` : ""}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* controls always visible, no scrolling needed */}
      <div className="shrink-0 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] bg-slate-900 flex items-center gap-3">
        <button onClick={() => setShowEn((v) => !v)} data-testid="amigos-toggle-en"
                className={`h-9 px-3 rounded-full text-xs font-semibold ${
                  showEn ? "bg-white/15 text-white" : "bg-white/5 text-white/50"}`}>
          EN
        </button>
        <p className="text-[11px] text-white/50 flex-1 leading-tight">{statusText}</p>
        {handsFree ? (
          <button onClick={pause} data-testid="amigos-pause-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 text-white px-5 py-2.5 text-sm font-semibold">
            <Pause className="h-4 w-4" /> Pausar
          </button>
        ) : (
          <button onClick={resume} disabled={left === 0} data-testid="amigos-resume-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-[#ff731d] hover:bg-[#e5651a] disabled:opacity-40 text-white px-5 py-2.5 text-sm font-semibold">
            <Play className="h-4 w-4" /> Seguir
          </button>
        )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

/** Step 2: pick what to talk about (or let the friend surprise you). */
export const AmigosTopics = ({ character, onStart, onBack }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/amigos/topics", { params: { character_id: character.id } })
      .then((r) => setData(r.data))
      .catch(() => setData({ kinds: {}, topics: [] }));
  }, [character.id]);

  const groups = Object.entries(data?.kinds || {}).map(([kind, label]) => ([
    label, (data?.topics || []).filter((t) => t.kind === kind),
  ])).filter(([, list]) => list.length);

  return (
    <div className="rounded-3xl bg-white border border-slate-200 p-6" data-testid="amigos-topics">
      <div className="flex items-center gap-3">
        <img src={character.image} alt={character.name} className="h-12 w-12 rounded-full object-cover" />
        <div className="flex-1">
          <p className="font-display text-lg font-bold text-slate-900">
            ¿De qué querés hablar con {character.name}?
          </p>
          <p className="text-xs text-slate-500">Elegí un tema o dejá que {character.name} elija.</p>
        </div>
        <button onClick={onBack} data-testid="amigos-topics-back"
                className="text-xs text-slate-500 hover:text-[#004aad] underline">Otro amigo</button>
      </div>

      <button onClick={() => onStart("surprise")} data-testid="amigos-topic-surprise"
              className="mt-5 w-full rounded-2xl bg-[#004aad] text-white px-5 py-4 text-left hover:bg-[#003c8c] transition-colors">
        <span className="font-semibold">Sorprendeme</span>
        <span className="block text-xs text-white/70 mt-0.5">
          {character.name} elige un tema nuevo cada vez
        </span>
      </button>

      {!data && <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-[#004aad]" /></div>}

      {groups.map(([label, list]) => (
        <div key={label} className="mt-6">
          <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">{label}</p>
          <div className="flex flex-wrap gap-2 mt-2">
            {list.map((t) => (
              <button key={t.id} onClick={() => onStart(t.id)} data-testid={`amigos-topic-${t.id}`}
                      className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 hover:border-[#ff731d] hover:text-[#ff731d] transition-colors">
                {t.title}
                {!!t.goals?.length && <span className="text-[10px] text-slate-400 ml-1.5">· {t.goals.length} retos</span>}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export const AmigosPicker = ({ characters, onPick }) => (
  <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="amigos-picker">
    {characters.map((c, i) => (
      <button key={c.id} onClick={() => { unlockAmigosAudio(); onPick(c); }} data-testid={`amigo-card-${c.id}`}
              style={{ animationDelay: `${i * 90}ms` }}
              className="group flex flex-col justify-start text-left rounded-3xl bg-white border border-slate-200 overflow-hidden shadow-[0_6px_24px_rgba(0,0,0,0.05)] hover:shadow-[0_14px_44px_rgba(0,0,0,0.12)] hover:-translate-y-1 transition-all duration-300">
        {/* the image overflows by 1px on every side: browser zoom can never show a white edge */}
        <div className="relative w-full shrink-0 aspect-square overflow-hidden bg-slate-200 leading-none">
          <img src={c.image} alt={c.name} loading="lazy"
               className="absolute -inset-px block h-[calc(100%+2px)] w-[calc(100%+2px)] object-cover group-hover:scale-105 transition-transform duration-500" />
        </div>
        <div className="p-4">
          <p className="font-display text-xl font-bold" style={{ color: c.color }}>{c.name}</p>
          <p className="text-xs uppercase tracking-wider text-slate-400">{c.animal} · {c.level}</p>
          <p className="text-sm text-slate-600 mt-2 leading-snug">{c.tagline}</p>
          <span className="inline-flex items-center gap-1 mt-3 text-sm font-semibold text-[#004aad]">
            Hablar con {c.name} →
          </span>
        </div>
      </button>
    ))}
  </div>
);
